import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [isHoveringTop, setIsHoveringTop] = useState(false);
  const lastScrollY = useRef(0);

  useEffect(() => {
    const controlHeader = () => {
      const currentScrollY = window.scrollY;
      const previousScrollY = lastScrollY.current;

      if (currentScrollY < 100) {
        // Always show header at top of page
        setIsVisible(true);
      } else if (currentScrollY > previousScrollY + 5) {
        // Scrolling down - hide header
        setIsVisible(false);
      } else if (currentScrollY < previousScrollY - 5) {
        // Scrolling up - show header
        setIsVisible(true);
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', controlHeader, { passive: true });
    return () => window.removeEventListener('scroll', controlHeader);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  return (
    <>
      {/* Hover detection area at top of page */}
      <div 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: '50px',
          zIndex: 999,
          pointerEvents: 'none'
        }}
        onMouseEnter={() => setIsHoveringTop(true)}
        onMouseLeave={() => setIsHoveringTop(false)}
      />

      <header className="site-header" style={{
        background: 'var(--bg-primary)',
        borderBottom: '1px solid var(--border-subtle)',
        padding: '16px 7.6923%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        position: 'fixed',
        top: isVisible || isHoveringTop ? '0' : '-200px',
        width: '100%',
        height: '180px',
        zIndex: 1000,
        boxSizing: 'border-box',
        transition: 'top 0.3s ease-in-out',
        boxShadow: isVisible || isHoveringTop ? '0 2px 10px rgba(0, 0, 0, 0.3)' : 'none'
      }}>
      <Link to="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '15px' }}>
        <img 
          className="site-logo"
          src="https://customer-assets.emergentagent.com/job_fd78a43a-475d-4d4e-ba79-642a920f6f2b/artifacts/wir97gbm_VDM%20Final%20Logo.png" 
          alt="VDM Business Innovations" 
          style={{
            height: '144px',
            objectFit: 'contain'
          }}
        />
        <div className="site-brand-text" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <div style={{
            fontSize: '28px',
            fontWeight: '700',
            color: '#B8860B',
            letterSpacing: '-0.3px',
            textShadow: '0 1px 2px rgba(184, 134, 11, 0.3)'
          }}>
            VDM Business Innovations
          </div>
          <div style={{
            fontSize: '14px',
            fontWeight: '500',
            color: '#B8860B',
            letterSpacing: '0.5px',
            opacity: 0.85
          }}>
            Transforming Industries. Touching Lives.
          </div>
        </div>
      </Link>

      {/* Desktop Navigation */}
      <nav style={{
        display: 'flex',
        alignItems: 'center',
        gap: '32px'
      }} className="desktop-nav">
        <button onClick={() => scrollToSection('about')} style={{
          background: 'none',
          border: 'none',
          color: 'var(--text-muted)',
          fontSize: '18px',
          fontWeight: '400',
          cursor: 'pointer',
          transition: 'color 0.3s ease'
        }}
        onMouseEnter={(e) => e.target.style.color = 'var(--text-primary)'}
        onMouseLeave={(e) => e.target.style.color = 'var(--text-muted)'}
        >
          About
        </button>
        <button onClick={() => scrollToSection('solutions')} style={{
          background: 'none',
          border: 'none',
          color: 'var(--text-muted)',
          fontSize: '18px',
          fontWeight: '400',
          cursor: 'pointer',
          transition: 'color 0.3s ease'
        }}
        onMouseEnter={(e) => e.target.style.color = 'var(--text-primary)'}
        onMouseLeave={(e) => e.target.style.color = 'var(--text-muted)'}
        >
          Solutions
        </button>
        <button onClick={() => scrollToSection('industries')} style={{
          background: 'none',
          border: 'none',
          color: 'var(--text-muted)',
          fontSize: '18px',
          fontWeight: '400',
          cursor: 'pointer',
          transition: 'color 0.3s ease'
        }}
        onMouseEnter={(e) => e.target.style.color = 'var(--text-primary)'}
        onMouseLeave={(e) => e.target.style.color = 'var(--text-muted)'}
        >
          Industries
        </button>
        <button onClick={() => scrollToSection('contact')} style={{
          background: 'none',
          border: 'none',
          color: 'var(--text-muted)',
          fontSize: '18px',
          fontWeight: '400',
          cursor: 'pointer',
          transition: 'color 0.3s ease'
        }}
        onMouseEnter={(e) => e.target.style.color = 'var(--text-primary)'}
        onMouseLeave={(e) => e.target.style.color = 'var(--text-muted)'}
        >
          Contact
        </button>
      </nav>

      {/* Mobile Menu Button */}
      <button 
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        style={{
          display: 'none',
          background: 'none',
          border: 'none',
          color: 'var(--text-primary)',
          cursor: 'pointer',
          padding: '8px'
        }}
        className="mobile-menu-btn"
      >
        {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div style={{
          position: 'absolute',
          top: '180px',
          left: 0,
          right: 0,
          background: 'var(--bg-primary)',
          borderBottom: '1px solid var(--border-subtle)',
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '20px'
        }} className="mobile-nav">
          <button onClick={() => scrollToSection('about')} style={{
            background: 'none',
            border: 'none',
            color: 'var(--text-muted)',
            fontSize: '18px',
            textAlign: 'left',
            cursor: 'pointer'
          }}>
            About
          </button>
          <button onClick={() => scrollToSection('solutions')} style={{
            background: 'none',
            border: 'none',
            color: 'var(--text-muted)',
            fontSize: '18px',
            textAlign: 'left',
            cursor: 'pointer'
          }}>
            Solutions
          </button>
          <button onClick={() => scrollToSection('industries')} style={{
            background: 'none',
            border: 'none',
            color: 'var(--text-muted)',
            fontSize: '18px',
            textAlign: 'left',
            cursor: 'pointer'
          }}>
            Industries
          </button>
          <button onClick={() => scrollToSection('contact')} style={{
            background: 'none',
            border: 'none',
            color: 'var(--text-muted)',
            fontSize: '18px',
            textAlign: 'left',
            cursor: 'pointer'
          }}>
            Contact
          </button>
        </div>
      )}

      <style>{`
        @media (max-width: 1024px) {
          .site-header { height: 120px !important; padding: 12px 5% !important; }
          .site-logo { height: 96px !important; }
          .site-brand-text > div:first-child { font-size: 20px !important; }
          .site-brand-text > div:last-child { font-size: 12px !important; }
        }
        @media (max-width: 768px) {
          .site-header { height: 88px !important; padding: 10px 5% !important; }
          .site-logo { height: 68px !important; }
          /* Hide side wordmark on mobile — logo already contains it */
          .site-brand-text { display: none !important; }
          .desktop-nav {
            display: none !important;
          }
          .mobile-menu-btn {
            display: block !important;
          }
          .mobile-nav {
            top: 88px !important;
          }
        }
      `}</style>
    </header>
    </>
  );
};

export default Header;